import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

// ============================================================
// MOCKUP A — Rotating Feature Showcase
// A device-frame in the hero that cycles every 4s through three
// real product surfaces. Pure CSS transitions, no carousel lib.
// ============================================================

const SCREENS = [
  { id: "scorecard", label: "Your eclectic scorecard" },
  { id: "leaderboard", label: "Live leaderboard" },
  { id: "certificate", label: "Win → certificate" },
];

export default function HeroMockup1() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-app">
      <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo variant="icon" size={28} />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </div>
          <span className="text-[12px] text-ink-soft">Mockup A · Rotating showcase</span>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-5 sm:px-8">
        <section className="grid lg:grid-cols-12 gap-8 lg:gap-16 pt-12 sm:pt-20 pb-16">
          <div className="lg:col-span-7">
            <p className="ge-eyebrow mb-4">Golf Eclectic · multi-round format</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-ink leading-[1.05]">
              Your best round, <span className="text-accent">one hole at a time.</span>
            </h1>
            <p className="text-[17px] sm:text-lg text-ink-muted mt-6 max-w-2xl leading-relaxed">
              Enter each round from the course. We keep the best of every hole and
              watch your perfect round take shape over the competition.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-2.5">
              <button className="ge-btn-primary !px-5 !py-3 !text-[14.5px] justify-center">Sign in <ArrowRight className="w-4 h-4" /></button>
              <button className="ge-btn-outline !px-5 !py-3 !text-[14.5px] justify-center">Continue with Google</button>
            </div>
          </div>

          {/* Device frame */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto" style={{ maxWidth: 360 }}>
              {/* Phone bezel */}
              <div className="relative rounded-[36px] bg-ink p-2.5 shadow-2xl">
                <div className="relative rounded-[28px] bg-white overflow-hidden" style={{ aspectRatio: "9/19" }}>
                  {/* notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-ink rounded-full z-20" />
                  {/* screens stack with crossfade */}
                  {SCREENS.map((s, i) => (
                    <div
                      key={s.id}
                      className="absolute inset-0 transition-opacity duration-700"
                      style={{ opacity: i === idx ? 1 : 0 }}
                    >
                      <MockScreen kind={s.id} />
                    </div>
                  ))}
                </div>
              </div>
              {/* indicator dots */}
              <div className="flex items-center justify-center gap-1.5 mt-4">
                {SCREENS.map((s, i) => (
                  <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-accent" : "w-1.5 bg-line"}`} />
                ))}
              </div>
              <p className="text-[12px] text-ink-soft text-center mt-2">{SCREENS[idx].label}</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function MockScreen({ kind }) {
  if (kind === "scorecard") {
    return (
      <div className="absolute inset-0 pt-10 px-3 pb-3 bg-app overflow-hidden">
        <p className="text-[9px] uppercase tracking-[0.14em] text-ink-soft font-semibold">Your eclectic</p>
        <p className="text-[15px] font-semibold text-ink">Round 4 of 10</p>
        <div className="grid grid-cols-3 gap-1 mt-3">
          {Array.from({ length: 9 }).map((_, i) => {
            const score = [3, 4, 5, 4, 3, 4, 5, 4, 4][i];
            const par = [4, 4, 5, 4, 3, 4, 5, 4, 4][i];
            const diff = score - par;
            const cls = diff < 0 ? "bg-accent text-white" : diff === 0 ? "bg-white text-ink" : "bg-surface-2 text-ink-muted";
            return (
              <div key={i} className={`rounded-md py-2 text-center ${cls} border border-line`}>
                <div className="text-[8px] opacity-70">Hole {i + 1}</div>
                <div className="text-[14px] font-bold tabular-nums">{score}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 rounded-lg bg-ink text-white p-2.5">
          <div className="flex items-center justify-between text-[10px] text-white/60">
            <span>NET ECLECTIC</span><span>−4</span>
          </div>
          <div className="text-2xl font-semibold tabular-nums mt-0.5">32</div>
        </div>
      </div>
    );
  }
  if (kind === "leaderboard") {
    return (
      <div className="absolute inset-0 pt-10 px-3 pb-3 bg-app overflow-hidden">
        <p className="text-[9px] uppercase tracking-[0.14em] text-ink-soft font-semibold">Live</p>
        <p className="text-[15px] font-semibold text-ink">Spring Eclectic</p>
        <div className="mt-3 space-y-1">
          {[
            { r: 1, n: "M. Vance", s: 60, gold: true },
            { r: 2, n: "J. Chen", s: 61 },
            { r: 3, n: "T. Harris", s: 62 },
            { r: 4, n: "You", s: 66, you: true },
            { r: 5, n: "S. Lewis", s: 66 },
            { r: 6, n: "A. Patel", s: 67 },
          ].map((p) => (
            <div key={p.r} className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${p.you ? "bg-accent-tint-2" : "bg-white"}`}>
              <span className={`font-mono tabular-nums text-[11px] w-3 ${p.gold ? "text-warm font-bold" : "text-ink-muted"}`}>{p.r}</span>
              <span className="flex-1 text-[12px] font-medium text-ink truncate">{p.n}</span>
              <span className="font-mono tabular-nums text-[13px] font-semibold text-ink">{p.s}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // certificate
  return (
    <div className="absolute inset-0 pt-10 px-3 pb-3 bg-app overflow-hidden">
      <div className="mt-2 rounded-xl border-2 border-accent bg-white p-4 text-center shadow-sm">
        <p className="text-[8px] uppercase tracking-[0.2em] text-accent font-semibold">Champion</p>
        <p className="text-[10px] text-ink-soft mt-1">Spring Eclectic 2026</p>
        <div className="my-3 h-px bg-line" />
        <p className="text-[10px] text-ink-soft">Awarded to</p>
        <p className="text-[18px] font-semibold tracking-tight text-ink mt-1">Alex Mercer</p>
        <p className="text-[10px] text-ink-muted mt-2">Net eclectic <b className="text-ink">60</b></p>
        <div className="mt-3 inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-tint">
          <Logo variant="icon" size={26} />
        </div>
      </div>
    </div>
  );
}
